import React, { useCallback, useEffect, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import type { Workspace } from '../../../types/workspace'
import { AppDialog, QrCodeDisplay } from '../../Common'
import { getMyBookingQr } from '../../../services/opsBookingsService'

export interface MemberBookingQrModalProps {
  open: boolean
  onClose: () => void
  workspace: Workspace
}

export function MemberBookingQrModal({
  open,
  onClose,
  workspace,
}: MemberBookingQrModalProps): React.ReactElement {
  const { t } = useNls()
  const [qrPayload, setQrPayload] = useState<string>('')
  const [expiresAt, setExpiresAt] = useState<number>(0)
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0)
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const fetchQr = useCallback(async () => {
    if (!workspace.id) return
    setBusy(true)
    setErrorMsg(null)
    try {
      const res = await getMyBookingQr({ workspaceId: workspace.id })
      setQrPayload(res.token)
      setExpiresAt(res.expiresAt)
      setSecondsRemaining(Math.max(0, Math.floor((res.expiresAt - Date.now()) / 1000)))
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setErrorMsg(msg || t('NLSMemberBookingsErrorGeneric'))
    } finally {
      setBusy(false)
    }
  }, [workspace.id, t])

  useEffect(() => {
    let active = true
    if (!open || !workspace.id) return

    void Promise.resolve().then(async () => {
      if (!active) return
      await fetchQr()
    })

    return () => {
      active = false
    }
  }, [open, workspace.id, fetchQr])

  useEffect(() => {
    if (!open || expiresAt <= 0) return
    const interval = setInterval(() => {
      const rem = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
      setSecondsRemaining(rem)
    }, 1000)
    return () => clearInterval(interval)
  }, [open, expiresAt])

  const isExpired = secondsRemaining <= 0 && Boolean(qrPayload)
  const minutes = Math.floor(secondsRemaining / 60)
  const seconds = secondsRemaining % 60
  const timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`

  return (
    <AppDialog
      open={open}
      title={t('NLSMemberBookingsQrModalTitle')}
      onDismiss={onClose}
      actions={[
        {
          label: t('NLSCommonActionClose'),
          variant: 'secondary',
          onClick: onClose,
        },
        {
          label: t('NLSMemberBookingsQrRefreshAction'),
          variant: 'primary',
          disabled: busy,
          onClick: fetchQr,
        },
      ]}
    >
      <div className="memberBookingQrModal__content">
        {errorMsg && (
          <div className="memberLinkBookingsCard__error" role="alert">
            {errorMsg}
          </div>
        )}

        {qrPayload && !isExpired && (
          <>
            <QrCodeDisplay value={qrPayload} size={220} />
            <span className="memberBookingQrModal__countdown">
              {t('NLSMemberBookingsQrCountdown', { time: timeString })}
            </span>
          </>
        )}

        {isExpired && (
          <div className="memberBookingQrModal__expired" role="status">
            {t('NLSMemberBookingsQrExpired')}
          </div>
        )}

        {busy && <span className="memberBookingsListTab__empty">{t('NLSOpsPlaceScanLoading')}</span>}
      </div>
    </AppDialog>
  )
}
